/**
 * NotificationsSection — Account Settings section for push notification management.
 * Allows users to enable/disable push notifications from their settings.
 *
 * SSR-safe: uses useIsMounted to defer all dynamic rendering until after
 * hydration, preventing server/client branch divergence.
 *
 * iOS-aware: detects iOS Safari and shows step-by-step Add to Home Screen
 * instructions instead of a generic "unsupported" message.
 */
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectIsSubscribed,
    selectPermissionState,
    selectIsPrompting,
    promptForPermission,
} from '../../../slices/pushNotificationSlice.mjs';
import { unregisterToken } from '../../../services/pushNotificationService.mjs';
import useIsMounted from '../../../hooks/useIsMounted.mjs';
import { isIOSSafari, isPWAMode, canReceivePushNotifications } from '../../../utils/platformDetection.mjs';

const STORAGE_KEY = 'pushNotification_state';

export default function NotificationsSection() {
    const dispatch = useDispatch();
    const isSubscribed = useSelector(selectIsSubscribed);
    const permissionState = useSelector(selectPermissionState);
    const isPrompting = useSelector(selectIsPrompting);
    const [isDisabling, setIsDisabling] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);

    // SSR-safe: defer ALL dynamic status rendering until after mount.
    const hasMounted = useIsMounted();

    // Platform checks — only eval client-side after mount
    const iosDevice = hasMounted && isIOSSafari();
    const pwaMode = hasMounted && isPWAMode();
    const notificationsSupported = hasMounted && canReceivePushNotifications();

    // iOS Safari (not installed as PWA) — needs Add to Home Screen
    const needsIOSInstall = iosDevice && !pwaMode && !notificationsSupported;

    const handleEnable = useCallback(async () => {
        setStatusMessage(null);
        try {
            const result = await dispatch(promptForPermission({ category: 'settings' })).unwrap();
            if (result?.granted && result?.token) {
                setStatusMessage({ type: 'success', text: 'Push notifications enabled!' });
            } else if (result?.granted === false) {
                setStatusMessage({ type: 'error', text: 'Notification permission was denied. Check your browser settings.' });
            } else {
                setStatusMessage({ type: 'error', text: 'Push notifications aren\'t available on this device.' });
            }
        } catch {
            setStatusMessage({ type: 'error', text: 'Failed to enable notifications.' });
        }
    }, [dispatch]);

    const handleDisable = useCallback(async () => {
        setIsDisabling(true);
        setStatusMessage(null);
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            const parsed = stored ? JSON.parse(stored) : null;
            if (parsed?.fcmToken) {
                await unregisterToken(parsed.fcmToken);
            }
            localStorage.removeItem(STORAGE_KEY);
            setStatusMessage({ type: 'success', text: 'Push notifications disabled. Reloading...' });
            setTimeout(() => window.location.reload(), 1000);
        } catch {
            setStatusMessage({ type: 'error', text: 'Failed to disable notifications.' });
        } finally {
            setIsDisabling(false);
        }
    }, []);

    // Pre-mount: static shell that matches SSR output exactly
    if (!hasMounted) {
        return (
            <div className="settings-section" id="notifications">
                <div className="settings-section-header">
                    <h3 className="settings-section-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'text-bottom' }}>
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        Notifications
                    </h3>
                    <p className="settings-section-desc">
                        Manage push notification preferences for this device.
                    </p>
                </div>
                <div className="settings-group">
                    <div className="settings-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                        <div>
                            <span className="settings-row-label">Push Notifications</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Status badge logic
    const getStatusInfo = () => {
        if (needsIOSInstall) {
            return { label: 'Install App', className: 'install' };
        }
        if (!notificationsSupported) {
            return { label: 'Unsupported', className: 'unsupported' };
        }
        if (permissionState === 'denied') {
            return { label: 'Blocked', className: 'blocked' };
        }
        if (isSubscribed) {
            return { label: 'Enabled', className: 'enabled' };
        }
        return { label: 'Disabled', className: 'disabled' };
    };

    const status = getStatusInfo();

    return (
        <div className="settings-section" id="notifications">
            <div className="settings-section-header">
                <h3 className="settings-section-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'text-bottom' }}>
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    Notifications
                </h3>
                <p className="settings-section-desc">
                    Manage push notification preferences for this device.
                </p>
            </div>

            <div className="settings-group">
                <div className="settings-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    <div>
                        <span className="settings-row-label">Push Notifications</span>
                        <span className={`settings-notif-status ${status.className}`}>
                            {status.label}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {!isSubscribed && permissionState !== 'denied' && notificationsSupported && !needsIOSInstall && (
                            <button
                                className="settings-btn settings-btn-primary"
                                onClick={handleEnable}
                                disabled={isPrompting}
                            >
                                {isPrompting ? 'Enabling...' : 'Enable'}
                            </button>
                        )}
                        {isSubscribed && (
                            <button
                                className="settings-btn settings-btn-danger"
                                onClick={handleDisable}
                                disabled={isDisabling}
                            >
                                {isDisabling ? 'Disabling...' : 'Disable'}
                            </button>
                        )}
                    </div>
                </div>

                {/* iOS Safari — Add to Home Screen steps */}
                {needsIOSInstall && (
                    <div className="settings-ios-install">
                        <p className="settings-ios-install-desc">
                            To receive push notifications on iOS, install this app to your Home Screen:
                        </p>
                        <ol className="settings-ios-steps">
                            <li>
                                <span className="settings-ios-step-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                        <polyline points="16 6 12 2 8 6" />
                                        <line x1="12" y1="2" x2="12" y2="15" />
                                    </svg>
                                </span>
                                Tap the <strong>Share</strong> button in Safari
                            </li>
                            <li>
                                <span className="settings-ios-step-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                        <line x1="12" y1="8" x2="12" y2="16" />
                                        <line x1="8" y1="12" x2="16" y2="12" />
                                    </svg>
                                </span>
                                Scroll down and tap <strong>Add to Home Screen</strong>
                            </li>
                            <li>
                                <span className="settings-ios-step-icon">🔔</span>
                                Open the app from your Home Screen, then come back here to enable notifications
                            </li>
                        </ol>
                        <p className="settings-ios-install-link">
                            <a
                                href="https://support.apple.com/guide/iphone/open-as-web-app-iphea86e5236/26/ios/26"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Learn more on Apple Support ↗
                            </a>
                        </p>
                    </div>
                )}

                {permissionState === 'denied' && (
                    <p className="settings-notif-hint">
                        Notifications are blocked by your browser. To re-enable, update the site
                        permission in your browser&apos;s address bar or settings.
                    </p>
                )}

                {/* Generic unsupported (non-iOS) */}
                {!notificationsSupported && !needsIOSInstall && (
                    <p className="settings-notif-hint">
                        Push notifications aren&apos;t supported on this browser.
                    </p>
                )}

                {statusMessage && (
                    <p className={`settings-notif-hint ${statusMessage.type}`}>
                        {statusMessage.text}
                    </p>
                )}
            </div>
        </div>
    );
}
